const DB_NAME = 'vr-tour-editor';
const DB_VERSION = 1;
const ASSETS_STORE = 'assets';
const VERSIONS_STORE = 'versions';

export const LOCAL_DRAFT_ID = 'default';
export const MAX_LOCAL_VERSIONS = 24;

let databasePromise;

function requestAsPromise(request) {
  return new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error || new Error('本地存储操作失败'));
  });
}

function transactionAsPromise(transaction) {
  return new Promise((resolve, reject) => {
    transaction.oncomplete = () => resolve();
    transaction.onabort = () => reject(transaction.error || new Error('本地存储操作被中断'));
    transaction.onerror = () => reject(transaction.error || new Error('本地存储操作失败'));
  });
}

function openDatabase() {
  if (!window.indexedDB) {
    return Promise.reject(new Error('当前浏览器不支持本地草稿存储'));
  }

  if (!databasePromise) {
    databasePromise = new Promise((resolve, reject) => {
      const request = window.indexedDB.open(DB_NAME, DB_VERSION);
      request.onerror = () => reject(request.error || new Error('无法打开本地草稿存储'));
      request.onupgradeneeded = () => {
        const database = request.result;
        if (!database.objectStoreNames.contains(ASSETS_STORE)) {
          database.createObjectStore(ASSETS_STORE, { keyPath: 'id' });
        }
        if (!database.objectStoreNames.contains(VERSIONS_STORE)) {
          const store = database.createObjectStore(VERSIONS_STORE, { keyPath: 'id' });
          store.createIndex('draftId', 'draftId', { unique: false });
        }
      };
      request.onsuccess = () => resolve(request.result);
    });
  }

  return databasePromise;
}

function createId(prefix) {
  const random = window.crypto?.randomUUID?.() || Math.random().toString(36).slice(2, 10);
  return `${prefix}-${Date.now()}-${random}`;
}

function isBlobUrl(value) {
  return typeof value === 'string' && value.startsWith('blob:');
}

function snapshotMediaSource({ assetId, url }) {
  return {
    assetId: assetId || null,
    sourceUrl: assetId || isBlobUrl(url) ? null : url || null,
  };
}

export function createAssetId(kind = 'asset') {
  return createId(kind);
}

export function createDraftSnapshot({ panos, currentId, stationsByPano }) {
  return {
    format: 'vr-tour-editor-draft',
    version: 1,
    currentId,
    panos: (panos || []).map((pano) => ({
      id: pano.id,
      name: pano.name,
      sourceName: pano.sourceName || pano.name,
      width: pano.width || null,
      height: pano.height || null,
      wasResized: Boolean(pano.wasResized),
      ...snapshotMediaSource({ assetId: pano.assetId, url: pano.url }),
      entryAudioName: pano.entryAudioName || null,
      entryAudioSourceName: pano.entryAudioSourceName || pano.entryAudioName || null,
      ...Object.fromEntries(
        Object.entries(
          snapshotMediaSource({ assetId: pano.entryAudioAssetId, url: pano.entryAudioUrl })
        ).map(([key, value]) => [`entryAudio${key[0].toUpperCase()}${key.slice(1)}`, value])
      ),
    })),
    stationsByPano: Object.fromEntries(
      Object.entries(stationsByPano || {}).map(([panoId, stations]) => [
        panoId,
        (stations || []).map((station) => ({
          id: station.id,
          name: station.name,
          yaw: station.yaw,
          pitch: station.pitch,
          targetPanoId: station.targetPanoId,
          audioName: station.audioName || null,
          audioSourceName: station.audioSourceName || station.audioName || null,
          ...snapshotMediaSource({ assetId: station.audioAssetId, url: station.audioUrl }),
        })),
      ])
    ),
  };
}

export async function saveAsset(id, file) {
  if (!(file instanceof Blob)) {
    throw new Error('待保存的素材无效');
  }

  const database = await openDatabase();
  const transaction = database.transaction(ASSETS_STORE, 'readwrite');
  transaction.objectStore(ASSETS_STORE).put({
    id,
    blob: file,
    name: file.name || `${id}.bin`,
    type: file.type || 'application/octet-stream',
    lastModified: file.lastModified || Date.now(),
    size: file.size,
    createdAt: new Date().toISOString(),
  });
  await transactionAsPromise(transaction);
}

export async function getAsset(id) {
  if (!id) return null;

  const database = await openDatabase();
  const transaction = database.transaction(ASSETS_STORE, 'readonly');
  const record = await requestAsPromise(transaction.objectStore(ASSETS_STORE).get(id));
  await transactionAsPromise(transaction);

  if (!record?.blob) return null;
  return new File([record.blob], record.name, {
    type: record.type,
    lastModified: record.lastModified,
  });
}

export async function saveDraftVersion({ draftId = LOCAL_DRAFT_ID, label, snapshot }) {
  const database = await openDatabase();
  const version = {
    id: createId('version'),
    draftId,
    label: label || '自动保存',
    createdAt: new Date().toISOString(),
    snapshot,
  };
  const transaction = database.transaction(VERSIONS_STORE, 'readwrite');
  transaction.objectStore(VERSIONS_STORE).put(version);
  await transactionAsPromise(transaction);

  const versions = await listDraftVersions(draftId);
  const expired = versions.slice(MAX_LOCAL_VERSIONS);
  if (expired.length) {
    const trimTransaction = database.transaction(VERSIONS_STORE, 'readwrite');
    const store = trimTransaction.objectStore(VERSIONS_STORE);
    expired.forEach((item) => store.delete(item.id));
    await transactionAsPromise(trimTransaction);
  }

  await pruneUnusedAssets(draftId);

  return version;
}

export async function listDraftVersions(draftId = LOCAL_DRAFT_ID) {
  const database = await openDatabase();
  const transaction = database.transaction(VERSIONS_STORE, 'readonly');
  const index = transaction.objectStore(VERSIONS_STORE).index('draftId');
  const records = await requestAsPromise(index.getAll(IDBKeyRange.only(draftId)));
  await transactionAsPromise(transaction);
  return records.sort((left, right) => right.createdAt.localeCompare(left.createdAt));
}

export async function getDraftVersion(id) {
  const database = await openDatabase();
  const transaction = database.transaction(VERSIONS_STORE, 'readonly');
  const record = await requestAsPromise(transaction.objectStore(VERSIONS_STORE).get(id));
  await transactionAsPromise(transaction);
  return record || null;
}

export async function getLatestDraftVersion(draftId = LOCAL_DRAFT_ID) {
  const versions = await listDraftVersions(draftId);
  return versions[0] || null;
}

function collectAssetIds(snapshot, target) {
  (snapshot?.panos || []).forEach((pano) => {
    if (pano.assetId) target.add(pano.assetId);
    if (pano.entryAudioAssetId) target.add(pano.entryAudioAssetId);
  });
  Object.values(snapshot?.stationsByPano || {}).forEach((stations) => {
    (stations || []).forEach((station) => {
      if (station.assetId) target.add(station.assetId);
    });
  });
}

async function pruneUnusedAssets(draftId) {
  const database = await openDatabase();
  const versions = await listDraftVersions(draftId);
  const usedAssetIds = new Set();
  versions.forEach((version) => collectAssetIds(version.snapshot, usedAssetIds));

  const transaction = database.transaction(ASSETS_STORE, 'readwrite');
  const store = transaction.objectStore(ASSETS_STORE);
  const assets = await requestAsPromise(store.getAll());
  assets.forEach((asset) => {
    if (!usedAssetIds.has(asset.id)) store.delete(asset.id);
  });
  await transactionAsPromise(transaction);
}

export async function deleteAssets(assetIds) {
  const validIds = [...new Set((assetIds || []).filter(Boolean))];
  if (!validIds.length) return;

  const database = await openDatabase();
  const transaction = database.transaction(ASSETS_STORE, 'readwrite');
  const store = transaction.objectStore(ASSETS_STORE);
  validIds.forEach((id) => store.delete(id));
  await transactionAsPromise(transaction);
}

async function restoreMedia({ assetId, sourceUrl, name, sourceName }) {
  if (!assetId) {
    return {
      assetId: null,
      sourceFile: null,
      sourceName: sourceName || name || null,
      url: sourceUrl || null,
    };
  }

  const file = await getAsset(assetId);
  if (!file) {
    throw new Error(`本地素材缺失：${name || assetId}`);
  }

  return {
    assetId,
    sourceFile: file,
    sourceName: sourceName || file.name,
    url: URL.createObjectURL(file),
  };
}

export async function restoreDraftSnapshot(snapshot) {
  if (!snapshot || snapshot.format !== 'vr-tour-editor-draft' || !Array.isArray(snapshot.panos)) {
    throw new Error('本地草稿格式无效');
  }

  const panos = [];
  const stationsByPano = {};

  try {
    for (const savedPano of snapshot.panos) {
      const image = await restoreMedia({
        assetId: savedPano.assetId,
        sourceUrl: savedPano.sourceUrl,
        name: savedPano.name,
        sourceName: savedPano.sourceName,
      });
      const entryAudio = await restoreMedia({
        assetId: savedPano.entryAudioAssetId,
        sourceUrl: savedPano.entryAudioSourceUrl,
        name: savedPano.entryAudioName,
        sourceName: savedPano.entryAudioSourceName,
      });
      panos.push({
        id: savedPano.id,
        name: savedPano.name,
        width: savedPano.width || null,
        height: savedPano.height || null,
        wasResized: Boolean(savedPano.wasResized),
        ...image,
        entryAudioAssetId: entryAudio.assetId,
        entryAudioFile: entryAudio.sourceFile,
        entryAudioUrl: entryAudio.url,
        entryAudioName: savedPano.entryAudioName || null,
        entryAudioSourceName: entryAudio.sourceName,
      });
    }

    for (const [panoId, savedStations] of Object.entries(snapshot.stationsByPano || {})) {
      const stations = [];
      stationsByPano[panoId] = stations;
      for (const savedStation of savedStations || []) {
        const audio = await restoreMedia({
          assetId: savedStation.assetId,
          sourceUrl: savedStation.sourceUrl,
          name: savedStation.audioName,
          sourceName: savedStation.audioSourceName,
        });
        stations.push({
          id: savedStation.id,
          name: savedStation.name,
          yaw: savedStation.yaw,
          pitch: savedStation.pitch,
          targetPanoId: savedStation.targetPanoId,
          audioAssetId: audio.assetId,
          audioFile: audio.sourceFile,
          audioUrl: audio.url,
          audioName: savedStation.audioName || null,
          audioSourceName: audio.sourceName,
        });
      }
    }

    return {
      panos,
      currentId: snapshot.currentId,
      stationsByPano,
    };
  } catch (error) {
    revokeProjectObjectUrls(panos, stationsByPano);
    throw error;
  }
}

export function revokeProjectObjectUrls(panos, stationsByPano) {
  const urls = new Set();
  (panos || []).forEach((pano) => {
    if (isBlobUrl(pano.url)) urls.add(pano.url);
    if (isBlobUrl(pano.entryAudioUrl)) urls.add(pano.entryAudioUrl);
  });
  Object.values(stationsByPano || {}).forEach((stations) => {
    (stations || []).forEach((station) => {
      if (isBlobUrl(station.audioUrl)) urls.add(station.audioUrl);
    });
  });
  urls.forEach((url) => URL.revokeObjectURL(url));
}

export function revokeObjectUrl(url) {
  if (isBlobUrl(url)) URL.revokeObjectURL(url);
}
